import React from 'react';

export default function PrivacyPolicy({ onBack }) {
  return (
    <div className="legal-page">
      <div className="legal-wrap">
        <div className="legal-header">
          <button className="legal-back" onClick={onBack}>← Back to CinemaAlert</button>
          <div className="legal-brand">🎬 Cinema Alert</div>
        </div>

        <h1 className="legal-title">Privacy Policy</h1>
        <p className="legal-updated">Last updated: March 2026</p>

        <section className="legal-section">
          <h2>1. What Data We Collect</h2>
          <p>When you create a Cinema Alert account, we collect the following personal information:</p>
          <ul>
            <li><strong>Full name</strong> — to personalize your alert notifications</li>
            <li><strong>Email address</strong> — to send ticket-availability alerts and account emails</li>
            <li><strong>Phone number</strong> — to send SMS alerts (only collected if you opt in to SMS or both)</li>
            <li><strong>Watchlist preferences</strong> — the movies you choose to track for ticket alerts</li>
            <li><strong>Notification preference</strong> — your choice of email, SMS, or both</li>
          </ul>
          <p>
            Your password is stored as a one-way bcrypt hash — we cannot read it. We do not
            collect payment information, social security numbers, or any government-issued identifiers.
          </p>
        </section>

        <section className="legal-section">
          <h2>2. How We Use Your Data</h2>
          <p>
            Your personal information is used <strong>only</strong> to operate the Cinema Alert
            service and send you the alerts you opted in to receive. Specifically:
          </p>
          <ul>
            <li>Sending you a notification when tickets go on sale for a movie on your watchlist</li>
            <li>Authenticating your account and keeping it secure</li>
            <li>Allowing you to manage your watchlist and notification preferences</li>
            <li>Sending transactional emails such as password reset links</li>
          </ul>
          <p>
            We will never use your information for marketing unrelated to Cinema Alert, and
            we will never send unsolicited promotional messages.
          </p>
        </section>

        <section className="legal-section">
          <h2>3. We Never Sell or Share Your Data</h2>
          <p>
            We do not sell, rent, trade, or otherwise transfer your personal information —
            including your name, email address, or phone number — to any third party for
            marketing purposes. Your data is used solely to deliver the Cinema Alert service to you.
          </p>
        </section>

        <section className="legal-section">
          <h2>4. Mobile Information</h2>
          <p>
            We collect phone numbers solely to send ticket alert SMS notifications that users
            have explicitly opted in to receive. Mobile information including phone numbers
            will never be shared with third parties or affiliates for marketing or promotional
            purposes. SMS consent is not a condition of purchase or account creation.
          </p>
        </section>

        <section className="legal-section">
          <h2>5. Third-Party Processors</h2>
          <p>
            We use the following service providers to deliver notifications on our behalf.
            These providers process your data only as instructed by us and are not permitted
            to use it for their own marketing purposes:
          </p>
          <ul>
            <li>
              <strong>SendGrid</strong> — used for email delivery. Your email address is
              transmitted to SendGrid solely to send you Cinema Alert notifications.
            </li>
            <li>
              <strong>Twilio</strong> — used for SMS delivery. Your phone number is
              transmitted to Twilio solely to send you Cinema Alert text messages.
            </li>
          </ul>
          <p>
            Both SendGrid and Twilio maintain their own privacy policies and security
            practices. No other third-party services receive your personal data.
          </p>
        </section>

        <section className="legal-section">
          <h2>6. How Long We Keep Your Data</h2>
          <p>
            We retain your personal information for as long as your account remains active.
            When you delete your account, all associated data — including your name, email
            address, phone number, and watchlist — is permanently deleted from our systems.
          </p>
        </section>

        <section className="legal-section">
          <h2>7. How to Delete Your Account and Data</h2>
          <p>
            You can request deletion of your account and all associated data at any time
            by emailing us at:
          </p>
          <p className="legal-contact">
            <a href="mailto:alerts@cinemaalert.io">alerts@cinemaalert.io</a>
          </p>
          <p>
            Please include "Delete My Account" in the subject line and send from the email
            address associated with your account. We will confirm deletion within 5 business days.
          </p>
        </section>

        <section className="legal-section">
          <h2>8. Cookies and Analytics</h2>
          <p>
            Cinema Alert does not use tracking cookies or third-party analytics tools.
            We do not run Google Analytics, Meta Pixel, or any other behavioral tracking
            or advertising technology. The only data stored in your browser is a login
            token (JWT) saved in <code>localStorage</code>, which is used solely to keep
            you signed in.
          </p>
        </section>

        <section className="legal-section">
          <h2>9. SMS Opt-Out</h2>
          <p>
            If you opted in to SMS notifications, you can unsubscribe at any time by
            replying <strong>STOP</strong> to any Cinema Alert text message. You will
            receive one final confirmation and no further SMS messages will be sent.
            You can re-enable SMS alerts at any time from your Account settings.
          </p>
        </section>

        <section className="legal-section">
          <h2>10. Security</h2>
          <p>
            We use industry-standard security practices including bcrypt password hashing,
            JWT-based authentication, and HTTPS for all data transmission. We do not store
            payment or financial information of any kind.
          </p>
        </section>

        <section className="legal-section">
          <h2>11. Changes to This Policy</h2>
          <p>
            We may update this Privacy Policy from time to time. We will notify registered
            users of material changes via email. The "Last updated" date at the top of this
            page reflects the most recent revision.
          </p>
        </section>

        <section className="legal-section">
          <h2>Contact</h2>
          <p>Questions about this Privacy Policy? Reach us at:</p>
          <p className="legal-contact">
            <a href="mailto:alerts@cinemaalert.io">alerts@cinemaalert.io</a>
          </p>
        </section>
      </div>

      <div className="legal-footer">
        <a href="/">Home</a>
        <span className="legal-footer-sep">·</span>
        <a href="/terms">Terms of Service</a>
        <span className="legal-footer-sep">·</span>
        <a href="/contact">Contact</a>
      </div>
    </div>
  );
}
